import { ref, computed } from 'vue'

export const DEV_USER_ID = import.meta.env.VITE_DEV_USER_ID
export const isDevMode = !!DEV_USER_ID

// Defense in depth: if dev mode somehow reaches a production bundle (e.g. a
// build that bypassed the vite.config guard), refuse to start. Better a blank
// page + Sentry alert than a silent dev@localhost session for every visitor.
if (isDevMode && import.meta.env.PROD) {
  throw new Error(
    'VITE_DEV_USER_ID was baked into a production build. Refusing to start to ' +
    'avoid signing visitors in as dev@localhost. Rebuild without VITE_DEV_USER_ID.'
  )
}

/**
 * Mock useAuth() that mimics Clerk's useAuth return shape for dev mode.
 * When VITE_DEV_USER_ID is set, the app skips Clerk entirely.
 */
export function useDevAuth() {
  const isLoaded = ref(true)
  const isSignedIn = ref(true)
  const getToken = ref(async () => DEV_USER_ID)

  return { isLoaded, isSignedIn, getToken }
}

/**
 * Mock useLinkflareAuth() return shape for dev mode.
 * Must mirror every property returned by the production useLinkflareAuth().
 */
export function useDevLinkflareAuth() {
  const isLoaded = ref(true)
  const isSignedIn = ref(true)
  return {
    // Match production interface exactly
    isLoaded,
    isSignedIn,
    isAuthenticated: computed(() => true),
    isLoading: computed(() => false),
    user: ref({ id: DEV_USER_ID, firstName: 'Dev', lastName: 'User', emailAddresses: [{ emailAddress: 'dev@localhost' }] }),
    userDisplayName: computed(() => 'Dev User'),
    userEmail: computed(() => 'dev@localhost'),
    getAuthToken: async () => 'dev-bypass-token',
    handleSignOut: async () => {
      if (typeof window !== 'undefined') {
        window.location.href = '/sign-in'
      }
      return true
    },
    // No Clerk user in dev mode: only the backend part of a deletion is real.
    accountDeletion: {
      needsReverification: () => false,
      reverify: async () => 'unavailable',
      deleteClerkUser: async () => {},
      suspendTokens: () => {},
      signOut: async (redirectUrl) => {
        if (typeof window !== 'undefined') {
          window.location.href = redirectUrl
        }
      }
    }
  }
}

/**
 * Anonymous (unauthenticated) useLinkflareAuth() return shape.
 * Used as a fallback when the Clerk Vue plugin is not installed in the
 * current app — for example, in the embed bundle (`embed-main.js`) which
 * deliberately omits Clerk. Mirrors every property returned by the
 * production useLinkflareAuth() so callers can use it without crashing.
 */
export function useAnonymousLinkflareAuth() {
  const isLoaded = ref(true)
  const isSignedIn = ref(false)
  return {
    isLoaded,
    isSignedIn,
    isAuthenticated: computed(() => false),
    isLoading: computed(() => false),
    user: ref(null),
    userDisplayName: computed(() => 'User'),
    userEmail: computed(() => ''),
    getAuthToken: async () => null,
    handleSignOut: async () => false,
    accountDeletion: {
      needsReverification: () => false,
      reverify: async () => 'unavailable',
      deleteClerkUser: async () => { throw new Error('Not signed in') },
      suspendTokens: () => {},
      signOut: async () => {}
    },
  }
}
